// src/redux/features/quoteApi.ts

import { invURL } from '../../components/Data/baseURL';

import { api } from '../services/api';
import { normalizePaginatedResponse, PaginatedResponse } from '../utils/pagination';

export interface Quote {
  id?: number | string;

  uf?: string;

  qn?: string | null;
  vn?: string | null;

  date_inv?: string | null;
  date_due?: string | null;

  bill_to?: Record<string, unknown> | null;

  person?: Record<string, unknown> | null;

  payment_term?: Record<string, unknown> | null;

  contract_terms?: Record<string, unknown> | null;

  currency?: Record<string, unknown> | null;

  status?: Record<string, unknown> | null;

  iteminv_invs?: Array<Record<string, unknown>>;

  comment_invs?: Array<Record<string, unknown>>;

  history_invs?: Array<Record<string, unknown>>;

  is_quote?: boolean | null;

  [key: string]: unknown;
}

export interface QuoteSearchParams {
  bill_to?: number | string;

  load_detail?: string;

  load_address?: string;

  unload_address?: string;

  country_load?: string;

  country_unload?: string;

  date_min?: string;

  date_max?: string;

  invoice_from?: string | number;

  invoice_to?: string | number;

  iteminv_invs?: number | string;

  currency?: number | string;

  comment_invs?: string;

  status?: number | string;

  limit?: number;

  offset?: number;

  ordering?: string;

  search?: string;
}

export type QuotePayload = Partial<Quote>;

interface UpdateQuoteArgs {
  id: number | string;
  body: QuotePayload;
}

export type PaginatedQuoteResponse = PaginatedResponse<Quote>;

const cleanParams = (params: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );

const buildQuotePayload = (body: QuotePayload): QuotePayload => {
  return {
    ...body,

    /*
     * Model/serializer field used when
     * creating or updating a quote.
     */
    is_quote: true,
  };
};

const getQuoteDetailUrl = (id: number | string) => {
  return `${invURL}${id}/`;
};

export const quoteApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getQuotes: builder.query<PaginatedQuoteResponse, QuoteSearchParams | void>({
      query: (params) => ({
        url: invURL,
        method: 'GET',

        /*
         * "quote" is the existing list-filter
         * query parameter used by your backend.
         */
        params: cleanParams({
          ...(params ?? {}),
          quote: true,
        }),
      }),

      transformResponse: normalizePaginatedResponse<Quote>,

      providesTags: (result) => [
        {
          type: 'Quote',
          id: 'LIST',
        },

        ...(result?.results ?? []).map((quote) => ({
          type: 'Quote' as const,

          id: quote.uf ?? quote.id,
        })),
      ],
    }),

    getQuote: builder.query<Quote, number | string>({
      query: (id) => ({
        url: getQuoteDetailUrl(id),

        method: 'GET',
      }),

      providesTags: (_result, _error, id) => [
        {
          type: 'Quote',
          id,
        },
      ],
    }),

    createQuote: builder.mutation<Quote, QuotePayload>({
      query: (body) => ({
        url: `${invURL}create/`,
        method: 'POST',

        body: buildQuotePayload(body),
      }),

      invalidatesTags: [
        {
          type: 'Quote',
          id: 'LIST',
        },
      ],
    }),

    updateQuote: builder.mutation<Quote, UpdateQuoteArgs>({
      query: ({ id, body }) => ({
        url: getQuoteDetailUrl(id),

        /*
         * Your old quote form used PUT.
         * Keep PUT while sending the
         * complete quote payload.
         */
        method: 'PUT',

        body: buildQuotePayload(body),
      }),

      invalidatesTags: (_result, _error, { id }) => [
        {
          type: 'Quote',
          id,
        },

        {
          type: 'Quote',
          id: 'LIST',
        },
      ],
    }),

    deleteQuote: builder.mutation<void, number | string>({
      query: (id) => ({
        url: getQuoteDetailUrl(id),

        method: 'DELETE',
      }),

      invalidatesTags: (_result, _error, id) => [
        {
          type: 'Quote',
          id,
        },

        {
          type: 'Quote',
          id: 'LIST',
        },
      ],
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetQuotesQuery,
  useGetQuoteQuery,
  useLazyGetQuoteQuery,
  useCreateQuoteMutation,
  useUpdateQuoteMutation,
  useDeleteQuoteMutation,
} = quoteApi;
